import { BaseElement } from '../../../base-element';
import type { EntrySummaryDto, EntryStatus, PdfStatus } from '../../../../api/types';
import '../../../ui/dt-badge';

interface BadgeConfig {
  variant: 'success' | 'warning' | 'error' | 'info';
  label: string;
}

export class EntryRow extends BaseElement {
  private _entry: EntrySummaryDto | null = null;

  get entry(): EntrySummaryDto | null {
    return this._entry;
  }

  set entry(value: EntrySummaryDto | null) {
    this._entry = value;
    if (this.isConnected) {
      this.updateRender();
    }
  }

  connectedCallback(): void {
    this.updateRender();
  }

  private getStatusBadge(status: EntryStatus): BadgeConfig {
    if (status === 'Submitted') {
      return { variant: 'success', label: 'Submitted' };
    }
    return { variant: 'info', label: 'Draft' };
  }

  private getPdfBadge(status: PdfStatus): BadgeConfig {
    switch (status) {
      case 'Success':
        return { variant: 'success', label: 'Ready' };
      case 'Failed':
        return { variant: 'error', label: 'Failed' };
      case 'InProgress':
        return { variant: 'warning', label: 'Processing' };
      case 'Queued':
      default:
        return { variant: 'info', label: 'Queued' };
    }
  }

  private formatDate(value?: string): string {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private handleRowClick = (): void => {
    if (!this._entry) return;
    this.emit('entry-selected', { 
      entryId: this._entry.entryId,
      trialEventId: this._entry.trialEventId 
    });
  };

  private handleRowKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleRowClick();
    }
  };

  private updateRender(): void {
    if (!this._entry) {
      this.render('');
      return;
    }

    const statusBadge = this.getStatusBadge(this._entry.status);
    const pdfBadge = this.getPdfBadge(this._entry.pdfStatus);
    const submittedAt = this.formatDate(this._entry.submittedAtUtc);

    this.render(`
      <div
        class="grid grid-cols-1 gap-3 px-4 py-3 text-sm text-neutral-700 sm:grid-cols-6 sm:items-center sm:gap-4 hover:bg-neutral-50 cursor-pointer"
        data-testid="entry-row"
        role="button"
        tabindex="0"
      >
        <div class="font-mono text-xs text-neutral-500" data-testid="entry-number">${this._entry.entryId}</div>
        <div class="truncate" data-testid="entry-handler">${this._entry.handlerEmail}</div>
        <div class="flex flex-col" data-testid="entry-dog">
          <span class="font-semibold text-neutral-900">${this._entry.dogCallName}</span>
          ${this._entry.dogRegisteredName ? `<span class="text-xs text-neutral-500">${this._entry.dogRegisteredName}</span>` : ''}
        </div>
        <div data-testid="entry-status">
          <dt-badge variant="${statusBadge.variant}">${statusBadge.label}</dt-badge>
        </div>
        <div data-testid="entry-pdf-status">
          <dt-badge variant="${pdfBadge.variant}">${pdfBadge.label}</dt-badge>
        </div>
        <div class="text-sm text-neutral-600" data-testid="entry-date">${submittedAt}</div>
      </div>
    `);

    const row = this.$('[data-testid="entry-row"]');
    if (row) {
      row.addEventListener('click', this.handleRowClick);
      row.addEventListener('keydown', this.handleRowKeydown);
    }
  }
}

customElements.define('dt-entry-row', EntryRow);
